/**
 * Questions fréquentes — dépliables, ou toutes ouvertes.
 *
 * ========================================================================
 * DEUX PRÉSENTATIONS, PARCE QUE LES DEUX SE JUSTIFIENT
 * ========================================================================
 *
 * `affichage: "deplie"` (défaut) — une ligne par question, dépliable au clic.
 * C'est ce qu'il faut quand les questions sont nombreuses : on balaie les
 * intitulés et on ouvre la sienne.
 *
 * `affichage: "ouvert"` — tout est lisible d'emblée, en cartes blanches.
 * C'est ce qu'il faut quand les questions sont peu nombreuses et que les
 * réponses sont courtes : replier trois réponses de deux lignes ajoute un
 * clic sans rien gagner. La page d'installation est exactement dans ce cas,
 * et c'est ainsi qu'elle était présentée.
 *
 * Le choix appartient donc à l'éditeur, page par page.
 *
 * ========================================================================
 * POURQUOI <details> ET NON UN ÉTAT REACT
 * ========================================================================
 *
 * Il fonctionne sans JavaScript, reste accessible au clavier et aux lecteurs
 * d'écran sans une ligne d'ARIA, et le navigateur sait le rechercher avec
 * Ctrl+F même replié. En mode « ouvert », c'est une simple carte : pas de
 * mécanique là où il n'y a rien à ouvrir.
 */
const FaqBloc = ({ bloc }) => {
    const questions = Array.isArray(bloc?.questions) ? bloc.questions : [];
    if (questions.length === 0) return null;

    const toutOuvert = String(bloc.affichage || '').toLowerCase() === 'ouvert';

    // LA TAILLE GRANDE : la FAQ de PricingPage, classe pour classe. Elle
    // porte sa propre largeur (max-w-3xl) et ses marges (py-16) — faite pour
    // la mise en page pleine largeur, où aucune carte ne l'encadre.
    const grande = bloc.taille === 'grande';

    // LE TITRE DE LA FAQ OUVERTE : celui de InstallAppPage, classe pour
    // classe. Il ajoutait text-2xl par-dessus text-xl — deux tailles dont
    // l'ordre du CSS decidait seul — et gardait le mb-5 de la version
    // repliee, plus serre que le mb-8 de la page d'origine.
    let classesTitre = 'text-xl font-bold text-gray-900 mb-5';
    if (grande) {
        classesTitre = 'text-3xl font-bold text-center text-gray-900 mb-12';
    } else if (toutOuvert) {
        classesTitre = 'text-2xl font-bold text-gray-900 text-center mb-8';
    }

    return (
        <section
            className={grande ? 'max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16' : 'mb-10'}
            data-testid="bloc-faq"
        >
            {bloc.titre && (
                <h2 className={classesTitre}>
                    {bloc.titre}
                </h2>
            )}

            <div className={grande ? 'space-y-6' : 'space-y-4'}>
                {questions.map((q, i) => (
                    toutOuvert ? (
                        <div key={i} className={`bg-white rounded-xl ${grande ? 'p-6' : 'p-5'} shadow-sm border border-gray-100`}>
                            <h3 className="font-semibold text-gray-900 mb-2">{q.question}</h3>
                            {q.reponse && (
                                <p className={grande ? 'text-gray-600 text-sm' : 'text-gray-600'}>{q.reponse}</p>
                            )}
                        </div>
                    ) : (
                        <details key={i} className="group rounded-xl border border-gray-200 bg-white">
                            <summary className="cursor-pointer list-none px-4 py-3 font-medium text-gray-900 flex items-center justify-between gap-3">
                                <span>{q.question}</span>
                                <span className="text-gray-400 transition-transform group-open:rotate-45">+</span>
                            </summary>
                            {q.reponse && (
                                <div className="px-4 pb-4 text-sm text-gray-600">{q.reponse}</div>
                            )}
                        </details>
                    )
                ))}
            </div>
        </section>
    );
};

export default FaqBloc;
